import React, { Component } from 'react';
import { myDepartures, downloadDeparture, importDeparture, restoreDeparture } from '../../../_services/clientDeparture.service';
import './DepartedClientsCard.css';

/**
 * "Your clients from <salon>" — a self-employed stylist's clients after they
 * left a salon (BE salonClient/departureHandover.js).
 *
 *   mode="salon"    Clients page at the salon they work at now: only shows
 *                   something to DO (bring them here, or restore them on a
 *                   rejoin). Download sits alongside.
 *   mode="account"  Their account with no salon (they land on Bookings):
 *                   every departure, to download.
 *
 * Renders nothing when there is nothing to show, and on any load error.
 */

const fmtDate = (d) => {
    try { return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }); }
    catch (e) { return ''; }
};
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

function importSummary(r) {
    const parts = [];
    if (r.added) parts.push(`${plural(r.added, 'client', 'clients')} added to your list.`);
    if (r.coHeld) parts.push(`${plural(r.coHeld, 'client was', 'clients were')} already at this salon. They're on your list too, with your notes.`);
    if (r.alreadyYours) parts.push(`${plural(r.alreadyYours, 'client was', 'clients were')} already on your list.`);
    if (r.failed) parts.push(`${plural(r.failed, 'client', 'clients')} couldn't be added. You can still download them.`);
    return parts.join(' ') || 'Nothing new to add.';
}

function restoreSummary(r) {
    const parts = [];
    if (r.restored) parts.push(`${plural(r.restored, 'client is', 'clients are')} back on your list.`);
    if (r.taken) parts.push(`${plural(r.taken, 'client is', 'clients are')} the salon's now. You share them again, with your notes.`);
    if (r.already) parts.push(`${plural(r.already, 'client was', 'clients were')} already on your list.`);
    if (r.gone) parts.push(`${plural(r.gone, 'client is', 'clients are')} no longer here. You can still download them.`);
    return parts.join(' ') || 'Nothing to restore.';
}

class DepartedClientsCard extends Component {
    state = { items: [], busy: null, done: {}, error: {} };

    componentDidMount() {
        this._alive = true;
        this.load();
    }

    componentWillUnmount() { this._alive = false; }

    load = () => {
        myDepartures()
            .then((items) => { if (this._alive) this.setState({ items }); })
            .catch(() => { if (this._alive) this.setState({ items: [] }); });
    };

    run = (id, kind) => {
        const fn = kind === 'import' ? importDeparture : kind === 'restore' ? restoreDeparture : downloadDeparture;
        this.setState({ busy: id + ':' + kind, error: { ...this.state.error, [id]: null } });
        fn(id)
            .then((r) => {
                if (!this._alive) return;
                const msg = kind === 'import' ? importSummary(r) : kind === 'restore' ? restoreSummary(r) : null;
                this.setState({ busy: null, done: msg ? { ...this.state.done, [id]: msg } : this.state.done });
                if (kind !== 'download') {
                    this.load();
                    if (this.props.onChanged) this.props.onChanged();
                }
            })
            .catch((e) => {
                if (this._alive) this.setState({ busy: null, error: { ...this.state.error, [id]: e.message } });
            });
    };

    renderOne(d) {
        const { busy, done, error } = this.state;
        const isBusy = (k) => busy === d.id + ':' + k;
        const anyBusy = !!busy;
        return (
            <div className="cdep-card" key={d.id}>
                <div className="cdep-head">
                    <h3 className="cdep-title">Your clients from {d.salonName}</h3>
                    <p className="cdep-sub">
                        {plural(d.total, 'client', 'clients')} came with you when you left on {fmtDate(d.at)}.
                        {' '}Your own notes and contact details are included. The salon's notes are not.
                    </p>
                </div>
                {d.canRestore && (
                    <p className="cdep-text">
                        You're back at {d.salonName}. You can put these clients back on your list here
                        {d.restoreUntil ? ` until ${fmtDate(d.restoreUntil)}` : ''}.
                    </p>
                )}
                {d.canImport && (
                    <p className="cdep-text">
                        Add them to your list here as your own clients, with your notes. Nothing changes at {d.salonName}.
                    </p>
                )}
                {done[d.id] && <p className="cdep-done" role="status">{done[d.id]}</p>}
                {error[d.id] && <p className="cdep-error" role="alert">{error[d.id]}</p>}
                <div className="cdep-actions">
                    {d.canRestore && (
                        <button type="button" className="cdep-btn cdep-btn--primary" disabled={anyBusy} onClick={() => this.run(d.id, 'restore')}>
                            {isBusy('restore') ? 'Restoring…' : 'Restore my clients'}
                        </button>
                    )}
                    {d.canImport && (
                        <button type="button" className="cdep-btn cdep-btn--primary" disabled={anyBusy} onClick={() => this.run(d.id, 'import')}>
                            {isBusy('import') ? 'Adding…' : `Add ${plural(d.toImport, 'client', 'clients')} to my list`}
                        </button>
                    )}
                    <button type="button" className="cdep-btn" disabled={anyBusy} onClick={() => this.run(d.id, 'download')}>
                        {isBusy('download') ? 'Preparing…' : 'Download (CSV)'}
                    </button>
                </div>
            </div>
        );
    }

    render() {
        const { mode } = this.props;
        const { items, done } = this.state;
        // At a salon: only what there is something to do with (or just did).
        const shown = mode === 'salon' ? items.filter((d) => d.canImport || d.canRestore || done[d.id]) : items;
        if (!shown.length) return null;
        return <div className="cdep-wrap">{shown.map((d) => this.renderOne(d))}</div>;
    }
}

export default DepartedClientsCard;
